import { Download, Pencil, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { SourceOperationsSheet } from "@/components/sources/source-operations-sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateSource,
  useDeleteSource,
  useRestoreConfigBackup,
  useRestoreSourcesFromYaml,
  useSources,
  useUpdateSource,
} from "@/hooks/useSources";
import { useOperatorStatus } from "@/hooks/useOperatorStatus";
import { useVendorPages } from "@/hooks/useVendorPages";
import type {
  OperatorSourceStatus,
  SignalAuditEvent,
  SourceConfig,
  SourceCreateRequest,
  SourceProcessing,
  SourceUpdateRequest,
  VendorPage,
} from "@/lib/api";
import { cn } from "@/lib/utils";

/** DB-backed admin for scraper_config. */
export default function AdminSourcesPage() {
  const sourcesQ = useSources();
  const statusQ = useOperatorStatus();
  const restoreConfig = useRestoreConfigBackup();
  const restoreInputRef = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState<SourceConfig | null>(null);
  const [creating, setCreating] = useState(false);
  const sourceStatusById = new Map(
    (statusQ.data?.source_states ?? []).map((state) => [state.source.id, state]),
  );

  const onRestoreConfigFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const backup = JSON.parse(await file.text()) as Record<string, unknown>;
      const confirmed = window.confirm(
        "Restore source config from this JSON backup? This upserts vendors, vendor pages, and scraper config.",
      );
      if (!confirmed) return;
      const response = await restoreConfig.mutateAsync(backup);
      const summary = Object.entries(response.restored)
        .map(([table, count]) => `${table}: ${count}`)
        .join(", ");
      toast.success(`restored config backup (${summary})`);
    } catch (err) {
      toast.error(`restore failed: ${(err as Error).message}`);
    } finally {
      if (restoreInputRef.current) {
        restoreInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-baseline justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold">Admin · Sources</h1>
          <p className="text-xs text-muted-fg">
            Signal source CRUD, vendor-page linking, and scraper JSON config.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/api/v1/backups/config"
            download
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium hover:bg-muted"
            title="Download DB-primary source configuration backup"
          >
            <Download className="size-3.5" />
            Export config
          </a>
          <a
            href="/api/v1/backups/full"
            download
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium hover:bg-muted"
            title="Download full Signals logical backup including inbox and audit history"
          >
            <Download className="size-3.5" />
            Export full
          </a>
          <input
            ref={restoreInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(event) => void onRestoreConfigFile(event.target.files?.[0])}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => restoreInputRef.current?.click()}
            disabled={restoreConfig.isPending}
            title="Restore source configuration from a Signals config JSON backup"
          >
            <Upload />
            Restore config
          </Button>
          <RestoreYamlButton />
          <Button
            size="sm"
            onClick={() => {
              setCreating(true);
              setEditing(null);
            }}
          >
            <Plus />
            New source
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {sourcesQ.isLoading ? (
          <div className="text-sm text-muted-fg">Loading…</div>
        ) : sourcesQ.isError ? (
          <div className="text-sm text-red-400">
            {(sourcesQ.error as Error).message}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {creating ? (
              <SourceForm
                initial={null}
                onCancel={() => setCreating(false)}
                onSaved={() => setCreating(false)}
              />
            ) : null}
            {(sourcesQ.data ?? []).map((source) =>
              editing?.id === source.id ? (
                <SourceForm
                  key={source.id}
                  initial={source}
                  onCancel={() => setEditing(null)}
                  onSaved={() => setEditing(null)}
                />
              ) : (
                <SourceRow
                  key={source.id}
                  source={source}
                  status={sourceStatusById.get(source.id)}
                  onEdit={() => {
                    setEditing(source);
                    setCreating(false);
                  }}
                />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Row ─────────────────────────────────────────────────────────────

function SourceRow({
  source,
  status,
  onEdit,
}: {
  source: SourceConfig;
  status: OperatorSourceStatus | undefined;
  onEdit: () => void;
}) {
  const del = useDeleteSource();
  const processing = processingFromSource(source);
  const workerState = workerStateForSource(source, status);
  return (
    <div
      className={cn(
        "flex items-start gap-4 rounded-md border border-border bg-muted/5 p-4",
        !source.is_active && "opacity-60",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-fg">{source.display_name}</span>
          <code className="text-[11px] text-muted-fg">{source.id}</code>
          {source.source_type ? (
            <Badge variant="outline" className="text-[10px]">
              {source.source_type}
            </Badge>
          ) : null}
          {!source.is_active ? (
            <Badge variant="outline" className="text-[10px]">inactive</Badge>
          ) : null}
          <Badge variant={processing.auto_extract ? "success" : "outline"} className="text-[10px]">
            {processing.auto_extract ? "auto analyze" : "manual analyze"}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            group {processing.group_size}
          </Badge>
          {workerState ? (
            <Badge variant={workerState.variant} className="text-[10px]" title={workerState.title}>
              {workerState.label}
            </Badge>
          ) : null}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-fg">
          {source.vendor ? (
            <span>vendor: <span className="text-fg">{source.vendor}</span></span>
          ) : null}
          <span>routing: <span className="text-fg">{source.routing}</span></span>
          <span>tier: <span className="text-fg">{source.confidence_tier}</span></span>
          <span>cadence: <span className="text-fg">{source.cadence}</span></span>
          <span>runtime: <span className="text-fg">{runtimeModeFromSource(source)}</span></span>
          {status?.last_audit_event ? (
            <span>
              last scrape: <span className="text-fg">{auditSummary(status.last_audit_event)}</span>
            </span>
          ) : null}
          {source.source_url ? (
            <span className="truncate">url: <a href={source.source_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">{source.source_url}</a></span>
          ) : null}
        </div>
        {source.description ? (
          <p className="mt-1 text-xs text-muted-fg">{source.description}</p>
        ) : null}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Pencil />
          Edit
        </Button>
        <SourceOperationsSheet source={source} />
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            if (!confirm(`Delete source '${source.id}'? Captured inbox content stays in the DB.`)) return;
            del.mutate(source.id, {
              onSuccess: () => toast.success(`deleted ${source.id}`),
              onError: (err) => toast.error(`delete failed: ${err.message}`),
            });
          }}
          disabled={del.isPending}
        >
          <Trash2 />
          Delete
        </Button>
      </div>
    </div>
  );
}

// ── Form ────────────────────────────────────────────────────────────

interface SourceFormProps {
  initial: SourceConfig | null;
  onCancel: () => void;
  onSaved: () => void;
}

function SourceForm({ initial, onCancel, onSaved }: SourceFormProps) {
  const create = useCreateSource();
  const update = useUpdateSource();
  const vendorPagesQ = useVendorPages();
  const isEdit = initial !== null;
  const initialConfig = initial?.config ?? {};

  const [form, setForm] = useState({
    id: initial?.id ?? "",
    display_name: initial?.display_name ?? "",
    vendor: initial?.vendor ?? "",
    source_type: initial?.source_type ?? "scraper",
    routing: initial?.routing ?? "manual_review",
    confidence_tier: initial?.confidence_tier ?? "medium",
    cadence: initial?.cadence ?? "manual",
    source_url: initial?.source_url ?? "",
    description: initial?.description ?? "",
    webhook_provider: initial?.webhook_provider ?? "",
    webhook_secret_ref: "",
    is_active: initial?.is_active ?? true,
  });
  const [vendorPageId, setVendorPageId] = useState(
    typeof initialConfig.vendor_page_id === "string" ? initialConfig.vendor_page_id : "",
  );
  const [configText, setConfigText] = useState(JSON.stringify(initialConfig, null, 2));

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    const config = parseConfig(configText);
    if (config === null) {
      toast.error("config must be a valid JSON object");
      return;
    }
    if (isEdit) {
      const patch: SourceUpdateRequest = {
        display_name: form.display_name,
        vendor: form.vendor,
        source_type: form.source_type,
        routing: form.routing,
        confidence_tier: form.confidence_tier,
        cadence: form.cadence,
        source_url: form.source_url,
        description: form.description,
        webhook_provider: form.webhook_provider,
        webhook_secret_ref: form.webhook_secret_ref || undefined,
        is_active: form.is_active,
        config,
      };
      update.mutate(
        { id: initial!.id, patch },
        {
          onSuccess: () => {
            toast.success(`saved ${initial!.id}`);
            onSaved();
          },
          onError: (err) => toast.error(`save failed: ${err.message}`),
        },
      );
    } else {
      const body: SourceCreateRequest = {
        id: form.id,
        display_name: form.display_name,
        vendor: form.vendor || null,
        source_type: form.source_type || null,
        routing: form.routing,
        confidence_tier: form.confidence_tier,
        cadence: form.cadence,
        source_url: form.source_url || null,
        description: form.description || null,
        webhook_provider: form.webhook_provider || null,
        webhook_secret_ref: form.webhook_secret_ref || null,
        is_active: form.is_active,
        config,
      };
      create.mutate(body, {
        onSuccess: () => {
          toast.success(`created ${form.id}`);
          onSaved();
        },
        onError: (err) => toast.error(`create failed: ${err.message}`),
      });
    }
  };

  const pending = create.isPending || update.isPending;
  const vendorPages = vendorPagesQ.data ?? [];
  const scrapePages = vendorPages.filter((page) => page.capabilities.scrape);

  return (
    <div className="rounded-md border border-border bg-muted/5 p-4">
      <h3 className="mb-3 text-sm font-semibold">
        {isEdit ? `Edit ${initial!.id}` : "New source"}
      </h3>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <Field label="id (slug)" required>
          <input
            type="text"
            value={form.id}
            onChange={(e) => set("id", e.target.value)}
            disabled={isEdit}
            placeholder="e.g. timothy_sykes_mc"
            className={inputClass}
          />
        </Field>
        <Field label="Display name" required>
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => set("display_name", e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Vendor">
          <input
            type="text"
            value={form.vendor}
            onChange={(e) => set("vendor", e.target.value)}
            placeholder="timothy_sykes"
            className={inputClass}
          />
        </Field>
        <Field label="Source type">
          <Select value={form.source_type} onValueChange={(v) => set("source_type", v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scraper">scraper</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label="Routing">
          <Select value={form.routing} onValueChange={(v) => set("routing", v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual_review">manual_review</SelectItem>
              <SelectItem value="suggest">suggest</SelectItem>
              <SelectItem value="auto">auto</SelectItem>
              <SelectItem value="ignore">ignore</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Confidence tier">
          <Select value={form.confidence_tier} onValueChange={(v) => set("confidence_tier", v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">high</SelectItem>
              <SelectItem value="medium">medium</SelectItem>
              <SelectItem value="low">low</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label="Cadence" help="manual, every_5min, hourly, …">
          <input
            type="text"
            value={form.cadence}
            onChange={(e) => set("cadence", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Source URL (vendor page)">
          <input
            type="text"
            value={form.source_url}
            onChange={(e) => set("source_url", e.target.value)}
            placeholder="https://..."
            className={inputClass}
          />
        </Field>

        <Field label="Vendor page">
          <Select
            value={vendorPageId || "__none__"}
            onValueChange={(value) => {
              const next = value === "__none__" ? "" : value;
              setVendorPageId(next);
              const page = vendorPages.find((candidate) => candidate.id === next);
              if (page) {
                set("vendor", page.vendor);
                set("source_url", page.url);
                setConfigText((current) =>
                  stringifyConfig({
                    ...parseConfig(current, { silent: true }),
                    vendor_page_id: page.id,
                    website_url: originFromUrl(page.url),
                  }),
                );
              }
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {scrapePages.map((page) => (
                <SelectItem key={page.id} value={page.id}>
                  {page.nav_label ?? page.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {vendorPageId ? (
          <VendorPageSummary
            className="col-span-2"
            page={vendorPages.find((page) => page.id === vendorPageId)}
          />
        ) : null}

        <Field label="Description" className="col-span-2">
          <textarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={2}
            className={cn(inputClass, "h-auto py-1.5")}
          />
        </Field>

        <Field
          label="Config JSON"
          className="col-span-2"
          help="Stored in scraper_config parser_config. Use vendor_page_id, website_url, parser filters, scraper_class, source_item_type, runtime, etc."
        >
          <textarea
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
            rows={8}
            spellCheck={false}
            className={cn(inputClass, "h-auto py-1.5 font-mono")}
          />
        </Field>

        <label className="col-span-2 flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
          />
          Active
        </label>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={pending}>
          {isEdit ? "Save" : "Create"}
        </Button>
      </div>
    </div>
  );
}

function VendorPageSummary({
  page,
  className,
}: {
  page: VendorPage | undefined;
  className?: string;
}) {
  if (!page) {
    return (
      <div className={cn("rounded-md border border-border px-3 py-2 text-xs text-red-300", className)}>
        Vendor page not found.
      </div>
    );
  }
  return (
    <div className={cn("rounded-md border border-border bg-muted/20 px-3 py-2 text-xs", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{page.label}</span>
        <Badge variant="outline">{page.page_type}</Badge>
        <Badge variant="outline">{page.access_mode ?? "open"}</Badge>
        {page.capabilities.scrape ? <Badge variant="success">scrape</Badge> : null}
        {page.capabilities.embed ? <Badge variant="secondary">embed</Badge> : null}
        {page.capabilities.warm_page ? <Badge variant="secondary">warm</Badge> : null}
      </div>
      <div className="mt-1 truncate text-muted-fg">{page.url}</div>
    </div>
  );
}

type RowBadgeVariant = "outline" | "success" | "warn" | "danger" | "info";

function processingFromSource(source: SourceConfig): SourceProcessing {
  const processing = source.config?.processing;
  if (!isRecord(processing)) {
    return { auto_extract: false, group_size: 1 };
  }
  return {
    auto_extract: processing.auto_extract === true,
    group_size: boundedNumber(processing.group_size, 1, 1, 20),
  };
}

function runtimeModeFromSource(source: SourceConfig): string {
  const runtime = source.config?.runtime;
  if (!isRecord(runtime) || typeof runtime.mode !== "string") {
    return "poll";
  }
  return runtime.mode;
}

function workerStateForSource(
  source: SourceConfig,
  status: OperatorSourceStatus | undefined,
): { label: string; variant: RowBadgeVariant; title: string } | null {
  if (!status) return null;
  if (status.worker.error) {
    return {
      label: "worker error",
      variant: "danger",
      title: status.worker.error,
    };
  }
  if (!status.worker.configured) {
    return {
      label: "worker not synced",
      variant: "warn",
      title: "This DB source is not configured in the content-scraper worker.",
    };
  }

  const expectedMode = runtimeModeFromSource(source);
  const cadenceMismatch = Boolean(status.worker.cadence && status.worker.cadence !== source.cadence);
  const modeMismatch = status.runtime.mode !== expectedMode;
  if (cadenceMismatch || modeMismatch) {
    return {
      label: "worker out of sync",
      variant: "warn",
      title: `Worker has ${status.worker.cadence ?? "no cadence"} / ${status.runtime.mode}; DB has ${source.cadence} / ${expectedMode}.`,
    };
  }

  if (status.worker.scheduled) {
    return {
      label: "worker scheduled",
      variant: "success",
      title: "Content-scraper worker has this source scheduled.",
    };
  }
  return {
    label: "worker synced",
    variant: "info",
    title: "Content-scraper worker has this source, but it is not currently scheduled.",
  };
}

function auditSummary(event: SignalAuditEvent): string {
  const when = new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(event.created_at));
  return `${event.status} ${when}`;
}

function boundedNumber(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(value, min), max);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseConfig(value: string, options?: { silent?: boolean }): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value || "{}") as unknown;
    if (parsed === null || Array.isArray(parsed) || typeof parsed !== "object") {
      return options?.silent ? {} : null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return options?.silent ? {} : null;
  }
}

function stringifyConfig(value: Record<string, unknown>): string {
  return JSON.stringify(value, null, 2);
}

function originFromUrl(value: string): string | undefined {
  try {
    return new URL(value).origin;
  } catch {
    return undefined;
  }
}

function RestoreYamlButton() {
  const restore = useRestoreSourcesFromYaml();
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => {
        const confirmed = window.confirm(
          "Restore source config from YAML backup? This overwrites DB-owned source config.",
        );
        if (!confirmed) {
          return;
        }
        restore.mutate(undefined, {
          onSuccess: (r) =>
            toast.success(
              `restored: +${r.added.length} ~${r.updated.length} -${r.deactivated.length}`,
            ),
          onError: (err) => toast.error(`restore failed: ${err.message}`),
        });
      }}
      disabled={restore.isPending}
      title="Restore source config from YAML backup"
    >
      <RefreshCw />
      Restore YAML
    </Button>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

const inputClass =
  "h-8 w-full rounded-md border border-border bg-transparent px-2 text-xs shadow-sm placeholder:text-muted-fg focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50";

function Field({
  label,
  required,
  help,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  help?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-fg">
        {label}
        {required ? <span className="text-red-400"> *</span> : null}
        {help ? <span className="ml-2 normal-case text-muted-fg/70">{help}</span> : null}
      </span>
      {children}
    </label>
  );
}
